"use client";

import { Icon } from "@afa-shared/afa-components";
import { gaTracking, getCurrentPageLocation, normalizeString } from "@lib/ga";
import { mapInsuranceName } from "@lib/insuranceMapping";
import clsx from "clsx";
import React, { type JSX, type ReactNode } from "react";

import styles from "./Accordion.module.css";

export type AccordionItem = {
  /** Unique anchor/id (optional). If omitted, falls back to index-based id. */
  id?: string;
  /** The clickable heading text. */
  question: string;
  /** The answer/body content. Can be rich ReactNode. */
  answer: ReactNode;
  /** Render open by default (SSR). */
  expanded?: boolean;
};

/** Declarative tracking config — data only, no callbacks needed from consumer. */
export interface AccordionTrackingConfig {
  /** Event name: "accordion_click" for default accordions, "faq_click" for FAQ variant. */
  event: "accordion_click" | "faq_click";
  /** Insurance value (from page context or active filter). Mapped to analytics name. */
  insurance?: string;
  /** Section/block title (used as `title` for faq_click). */
  sectionTitle?: string;
  /** Section/block index (used as `index` for faq_click). */
  sectionIndex?: number;
  /** Active subject filter (faq_click only). */
  subject?: string;
}

export interface AccordionDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Items to render. */
  items: AccordionItem[];
  /** Heading level used visually inside summary (optional). */
  headingTag?: keyof JSX.IntrinsicElements; // e.g., 'h2' | 'h3' | 'h4'
  /** Optional className passthrough. */
  className?: string;
  /** Variant style (default|"faq"). */
  variant?: "default" | "faq";
  /** Declarative tracking config. When set, fires GA event on item open. */
  tracking?: AccordionTrackingConfig;
}

/** Minimal slugifier to create stable DOM ids when item.id is missing. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** Fire GA event based on declarative tracking config. Exported for unit testing. */
export function trackItemOpen(
  config: AccordionTrackingConfig,
  item: AccordionItem,
  index: number,
): void {
  const pageLocation = getCurrentPageLocation();

  if (config.event === "accordion_click") {
    gaTracking({
      event: "accordion_click",
      event_parameters: {
        page_location: pageLocation,
        title: normalizeString(item.question) ?? "",
        index,
        insurance: normalizeString(config.insurance) ?? undefined,
      },
    });
  } else {
    gaTracking({
      event: "faq_click",
      event_parameters: {
        page_location: pageLocation,
        title: config.sectionTitle ?? "",
        index: config.sectionIndex,
        link_text: normalizeString(item.question) ?? "",
        insurance: mapInsuranceName(config.insurance) ?? undefined,
        subject: config.subject ?? undefined,
      },
    });
  }
}

export default function AccordionDetails({
  items,
  headingTag = "h3",
  variant = "default",
  tracking,
  ...divProps
}: AccordionDetailsProps) {
  const Heading: keyof JSX.IntrinsicElements = headingTag;
  const headingSizeClass =
    variant !== "faq"
      ? styles[`details-accordion__heading--${headingTag}` as keyof typeof styles] ||
        styles["details-accordion__heading--h3"]
      : undefined;
  const panelVariantClass =
    styles[`details-accordion__panel--${variant}` as keyof typeof styles] ||
    styles["details-accordion__panel--default"];

  return (
    <div
      {...divProps}
      className={clsx(
        styles["details-accordion"],
        { [styles.faq]: variant === "faq" },
        divProps.className,
      )}
    >
      {items.map((item, i) => {
        const domId = item.id ?? `${slugify(item.question)}-${i}`;
        return (
          <details
            key={domId}
            id={domId}
            className={styles["details-accordion__item"]}
            {...(item.expanded ? { open: true } : {})}
            onToggle={(e) => {
              if (e.currentTarget.open && tracking) {
                trackItemOpen(tracking, item, i);
              }
            }}
          >
            <summary
              className={clsx(styles["details-accordion__summary"], {
                [styles["details-accordion__summary--faq"]]: variant === "faq",
              })}
            >
              {variant === "faq" && (
                // TODO: Replace with <Icon> from @afa-shared/afa-components when question-mark icon is available
                <span className={styles["details-accordion__question-icon"]} aria-hidden="true">
                  ?
                </span>
              )}
              {/* Keep the user-visible label inside summary for native toggling */}
              <Heading className={clsx(styles["details-accordion__heading"], headingSizeClass)}>
                {item.question}
              </Heading>
              <span className={styles["details-accordion__icon-wrapper"]} aria-hidden="true">
                <Icon name={"add"} className={styles["details-accordion__icon--add"]} />
                <Icon name={"remove"} className={styles["details-accordion__icon--remove"]} />
              </span>
            </summary>

            {/* Panel body: use content-visibility to skip offscreen work.
               The intrinsic size is small to avoid layout jumps before paint. */}
            <div className={clsx(styles["details-accordion__panel"], panelVariantClass)}>
              {item.answer}
            </div>
          </details>
        );
      })}
    </div>
  );
}
